import {
  GetIssueResponseDtoApprovalStatusEnum,
  GetIssueResponseDtoStatusEnum,
} from '@issuemanager/api-client';
import {
  IssueApprovalStatusEnum,
  IssueStatusEnum,
} from '@issuemanager/constants';

type Params = {
  status: GetIssueResponseDtoStatusEnum | undefined;
  isInitiated?: boolean;
  approvalStatus?: GetIssueResponseDtoApprovalStatusEnum | undefined;
};

export const useIssueStatusInfo = ({
  status,
  isInitiated,
  approvalStatus,
}: Params): {
  isEditable: boolean;
  isInitiated?: boolean;
  isClosedOrDiscarded: boolean;
  isNotYetAssigned: boolean;
  isReadyToApprove: boolean;
} => {
  const issueStatusDefaultValues = {
    isEditable: false,
    isInitiated: false,
    isClosedOrDiscarded: false,
    isNotYetAssigned: false,
    isReadyToApprove: false,
  };

  if (!status) {
    return issueStatusDefaultValues;
  }

  const closedOrDiscardedOptions: GetIssueResponseDtoStatusEnum[] = [
    IssueStatusEnum.CLOSED,
    IssueStatusEnum.DISCARDED,
  ];

  const isClosedOrDiscarded = closedOrDiscardedOptions.includes(status);

  if (!approvalStatus) {
    return {
      ...issueStatusDefaultValues,
      isClosedOrDiscarded,
    };
  }

  const editableOptions: GetIssueResponseDtoStatusEnum[] = [
    GetIssueResponseDtoStatusEnum.Draft,
    GetIssueResponseDtoStatusEnum.Open,
    GetIssueResponseDtoStatusEnum.InProgress,
    GetIssueResponseDtoStatusEnum.RemediationPlanning,
    GetIssueResponseDtoStatusEnum.InRemediation,
  ];

  const isEditable = editableOptions.includes(status);

  const isNotYetAssigned =
    status === IssueStatusEnum.IN_REVIEW ||
    status === IssueStatusEnum.OPEN ||
    status === IssueStatusEnum.TO_BE_PUBLISHED ||
    status === IssueStatusEnum.PENDING_ACCEPTANCE;

  const isReadyToApprove = Boolean(
    status === IssueStatusEnum.TO_BE_APPROVED &&
      approvalStatus === IssueApprovalStatusEnum.WAITING_FOR_APPROVAL,
  );

  const initiatedByStatus: Partial<
    Record<GetIssueResponseDtoStatusEnum, boolean>
  > = {
    [IssueStatusEnum.IN_REMEDIATION]: true,
    [IssueStatusEnum.REMEDIATION_PLANNING]: false,
  };

  const resolvedIsInitiated = isInitiated ?? initiatedByStatus[status];

  return {
    isEditable,
    isInitiated: resolvedIsInitiated,
    isClosedOrDiscarded,
    isNotYetAssigned,
    isReadyToApprove,
  };
};
